import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Box,
  Alert
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../utils/currency';

const PriceDifferenceModal = ({ open, onClose, onConfirm, priceDifferences, loading = false }) => {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const getDifferenceStyles = (differencePercent) => {
    const numericDifference = Number(differencePercent ?? 0);

    if (Math.abs(numericDifference) <= 10) {
      return {};
    }

    return {
      color: numericDifference > 0 ? 'error.main' : 'success.main',
      fontWeight: 'bold'
    };
  };

  const handleConfirm = async () => {
    if (confirmed && !confirming && !loading) {
      setConfirming(true);
      try {
        await onConfirm();
        setConfirmed(false);
      } finally {
        setConfirming(false);
      }
    }
  };

  const handleClose = () => {
    if (confirming || loading) return;
    setConfirmed(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('price_difference_warning')}
        </Alert>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {t('price_difference_message')}
        </Typography>
        
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>{t('supply')}</strong></TableCell>
                <TableCell align="right"><strong>{t('last_price')}</strong></TableCell>
                <TableCell align="right"><strong>{t('new_price')}</strong></TableCell>
                <TableCell align="right"><strong>{t('difference')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {priceDifferences.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.supplyName}</TableCell>
                  <TableCell align="right">{formatMoney(item.lastPrice)}</TableCell>
                  <TableCell align="right">{formatMoney(item.newPrice)}</TableCell>
                  <TableCell align="right" sx={getDifferenceStyles(item.differencePercent)}>
                    {item.differencePercent > 0 ? '+' : ''}{item.differencePercent.toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                color="primary"
                disabled={confirming || loading}
              />
            }
            label={t('confirm_price_validation')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={confirming || loading}>{t('cancel')}</Button>
        <LoadingButton
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          loading={confirming || loading}
          disabled={!confirmed || confirming || loading}
        >
          {t('confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PriceDifferenceModal;
